
import { supabase } from "@/integrations/supabase/client";

/**
 * Uploads a file to Supabase storage and returns the file path
 */
export async function uploadFile(
  bucket: string,
  folderPath: string,
  file: File
): Promise<string> {
  const filePath = `${folderPath}/${file.name}`;
  
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, {
      upsert: true,
      cacheControl: '3600'
    });
  
  if (error) {
    throw error;
  }
  
  return data.path;
}

/**
 * Gets a public URL for a file in Supabase storage
 */
export async function getFileUrl(
  bucket: string,
  filePath: string
): Promise<string> {
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(filePath);
  
  return data.publicUrl;
}

/**
 * Removes a file from Supabase storage
 */
export async function deleteFile(
  bucket: string,
  filePath: string
): Promise<void> {
  const { error } = await supabase.storage
    .from(bucket)
    .remove([filePath]);
  
  if (error) {
    throw error;
  }
}

/**
 * Lists all files in a storage bucket path
 */
export async function listFiles(
  bucket: string,
  folderPath: string
): Promise<Array<{name: string, path: string}>> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .list(folderPath);
  
  if (error) {
    throw error;
  }
  
  // Filter out folders, only return files
  const files = data?.filter(item => !item.id.endsWith('/')) || [];
  
  return files.map(file => ({
    name: file.name,
    path: `${folderPath}/${file.name}`
  }));
}

/**
 * Checks if a file exists in Supabase storage
 */
export async function fileExists(
  bucket: string, 
  filePath: string
): Promise<boolean> {
  try {
    // Try to get metadata of the file
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, 1); // 1 second expiry just to check existence
    
    return !error && data !== null;
  } catch (error) {
    console.error("Error checking if file exists:", error);
    return false;
  }
}

/**
 * Gets file metadata from Supabase storage
 */
export async function getFileMetadata(
  bucket: string,
  filePath: string
): Promise<any> {
  try {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);
    
    // Try to get mime type from the file extension
    const extension = filePath.split('.').pop()?.toLowerCase();
    let contentType = 'application/octet-stream'; // default
    
    if (extension === 'pdf') contentType = 'application/pdf';
    else if (['jpg', 'jpeg'].includes(extension)) contentType = 'image/jpeg';
    else if (extension === 'png') contentType = 'image/png';
    else if (extension === 'mp4') contentType = 'video/mp4';
    else if (extension === 'mp3') contentType = 'audio/mpeg';
    
    return {
      url: data.publicUrl,
      contentType
    };
  } catch (error) {
    console.error("Error getting file metadata:", error);
    throw error;
  }
}
